import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp } from 'antd';

import { api } from '../../shared/api-client.js';
import type { ExportNotification } from './export-notifications.js';

interface ExportNotificationFeedOptions {
  readonly enabled: boolean;
  readonly refetchInterval: false | number;
}

export function useExportNotificationFeed({
  enabled,
  refetchInterval,
}: ExportNotificationFeedOptions) {
  const { message: messageApi } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const query = useQuery({
    enabled,
    queryFn: api.notifications,
    queryKey: ['notifications', 'list'],
    refetchInterval,
  });

  async function invalidateNotifications() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
    ]);
  }

  const showError = (error: unknown) => {
    void messageApi.error(error instanceof Error ? error.message : '操作失败，请稍后重试');
  };
  const markRead = useMutation({
    mutationFn: (item: ExportNotification) => api.markNotificationRead(item.id),
    onError: showError,
    onSuccess: invalidateNotifications,
  });
  const retry = useMutation({
    mutationFn: (item: ExportNotification) => api.retryExportTask(item.exportTaskId),
    onError: showError,
    onSuccess: async (_, item) => {
      if (!item.isRead) await api.markNotificationRead(item.id).catch(() => undefined);
      await invalidateNotifications();
      await queryClient.invalidateQueries({ queryKey: ['export-tasks'] });
      void messageApi.success('已创建新的重试任务');
    },
  });
  const download = useMutation({
    mutationFn: (item: ExportNotification) => api.exportTaskDownload(item.exportTaskId),
    onError: showError,
    onSuccess: async ({ url }, item) => {
      if (!item.isRead) await api.markNotificationRead(item.id).catch(() => undefined);
      await invalidateNotifications();
      window.location.assign(url);
    },
  });

  return { download, markRead, query, retry };
}
